import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LUPP_QUERY_KEYS } from '../constants.lupp';
import { LuppApi } from '../lupp.service';
import {
  UseGetOrdersParams,
  SearchOrdersRequest,
  SearchOrdersResponse,
  OrderDetailsResponse,
  UpdateOrderRequest,
  UpdateOrderResponse,
  UpdateFinanceProviderRequest,
  UpdateFinanceProviderResponse,
  DunsSearchResponse,
  BusinessSearchResult,
  ConfigurationItem,
  VehicleDetails,
  UploadOrderDocumentRequest,
  UploadOrderDocumentResponse,
  CustomerOverviewPayload,
  Invoice,
  CreateInvoiceRequest,
  CreateInvoiceResponse,
  RegistrationDeliveryDetails,
  PatchOrderDetailsRequest,
  GetFinalInvoicesResponse,
  GetFinalInvoicesParams,
  DownloadFinalInvoiceParams,
  OrderOfacDetails,
  OfacPartyDetails,
  ConfigurationLineItem,
  GenerateDeliveryAcceptancePreviewRequest,
  DeliveryAcceptancePreviewDto,
  DeliveryAcceptanceSecondSignerRequest,
  DeliveryAcceptanceSecondSignerResponse,
  ServiceCenter,
} from './dto.order';
import { formatString, formatToUTCDate, isNonNullableEntry } from '../../../../utils/tools';
import { getIdToken } from '@lucid/auth';
import { deduplicateFileReferences } from './transformers.order';
import { DOCUMENT_CATEGORY } from '../../../../constants/constants';

const INCLUDED_LABEL = 'Included';

// The order of configuration line items is determined by the sequence of typeIds defined in this array.
const configurationLineItemsDisplayOrder = [
  'LUCID_MODEL_VARIANT',
  'LUCID_DREAMDRIVE',
  'LUCID_TRIM',
  'LUCID_MODEL_YEAR',
  'LUCID_MODEL',
  'LUCID_COUNTRY',
  'LUCID_REGION',
  'LUCID_EDITION',
  'LUCID_EXT_COLOR',
  'LUCID_SECONDARY_EXT_COLOR',
  'LUCID_WHEELS',
  'LUCID_DRIVETRAIN',
  'LUCID_SEATING',
  'LUCID_ROOF',
  'LUCID_INT_THEME',
  'LUCID_APPEARANCE',
  'LUCID_SOUNDSYSTEM',
  'LUCID_UX',
];

const configurationLineItemsDisplayOrderMap = new Map(
  configurationLineItemsDisplayOrder.map((typeId, index) => [typeId, index])
);

const sortConfigurationLineItems = (
  lineItems: ConfigurationLineItem[]
): ConfigurationLineItem[] => {
  return [...lineItems].sort((a, b) => {
    const aIndex = configurationLineItemsDisplayOrderMap.get(a.typeId) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = configurationLineItemsDisplayOrderMap.get(b.typeId) ?? Number.MAX_SAFE_INTEGER;

    return aIndex - bIndex;
  });
};

const getConfigurationItemLabel = (price: ConfigurationLineItem['price']): string | number => {
  if (price === 0 || price === '0.00' || price === null || price === undefined)
    return INCLUDED_LABEL;
  return price;
};

const resolveLineItemDisplayName = (item: ConfigurationLineItem): string => {
  if (item.localeName != null && item.localeName !== '' && item.localeName !== item.typeId) {
    return item.localeName;
  }
  return item.technicalName;
};

const mapLineItemsToConfigItems = (lineItems: ConfigurationLineItem[]): ConfigurationItem[] =>
  sortConfigurationLineItems(lineItems).map((item) => {
    const displayName = resolveLineItemDisplayName(item);
    const name = item.typeId === 'LUCID_MODEL_YEAR' ? `Model Year ${displayName}` : displayName;
    return { name, label: getConfigurationItemLabel(item.price) };
  });

type FilteredOrderConfigurationDetails = NonNullable<
  OrderDetailsResponse['configurationDetails']
> & {
  configurationItems?: ConfigurationItem[];
};

const formatVehicleDetailsToConfigItems = (details?: VehicleDetails): ConfigurationItem[] => {
  if (!details) return [];

  // Exclude fields that are not required in the configuration item map list.
  // These keys belong to metadata or consolidated pricing results rendered elsewhere,
  // so they should not be converted into individual configuration rows.
  // - orderSubtotal, orderTotal: Consolidated pricing summary values
  // - imageUrl, shortCode: Vehicle metadata used by other UI elements
  // - createdDate, currencyCode: Order metadata not part of itemized configuration
  // - totalPriceInclOptions, totalPriceExclOptions: Aggregated totals, not per-item values
  const excludeFields = new Set([
    'imageUrl',
    'shortCode',
    'createdDate',
    'currencyCode',
    'totalPriceInclOptions',
    'totalPriceExclOptions',
    'orderSubtotal',
    'orderTotal',
  ]);

  return Object.entries(details)
    .filter((entry) => isNonNullableEntry<string | number>(entry, excludeFields))
    .map(([key, value]) => ({
      name: formatString(key),
      label: getConfigurationItemLabel(value),
    }));
};

const filterConfigurationDetails = (
  configurationDetails: OrderDetailsResponse['configurationDetails']
): FilteredOrderConfigurationDetails | undefined => {
  if (!configurationDetails) {
    return configurationDetails;
  }

  const lineItems = configurationDetails.lineItems ?? [];
  const modelLineItem = lineItems.find((item) => item.typeId === 'LUCID_MODEL');
  const modelVariantLineItem = lineItems.find((item) => item.typeId === 'LUCID_MODEL_VARIANT');

  const modelVariantLabel = [modelLineItem?.technicalName, modelVariantLineItem?.technicalName]
    .filter(Boolean)
    .join(' ');

  const toNumericPrice = (price: ConfigurationLineItem['price']): number => {
    if (typeof price === 'number') {
      return price;
    }

    if (typeof price === 'string') {
      const parsedPrice = Number(price);
      return Number.isFinite(parsedPrice) ? parsedPrice : 0;
    }

    return 0;
  };

  const modelVariantValue =
    toNumericPrice(modelLineItem?.price ?? null) +
    toNumericPrice(modelVariantLineItem?.price ?? null);

  const configurationItemsFromLineItems = mapLineItemsToConfigItems(lineItems);

  return {
    ...configurationDetails,
    configurationItems:
      configurationItemsFromLineItems.length > 0
        ? configurationItemsFromLineItems
        : formatVehicleDetailsToConfigItems(configurationDetails.vehicleDetails),
    modelVariantLabel,
    modelVariantValue,
  };
};

const filterOfacDetails = (response: OrderDetailsResponse): OrderOfacDetails | undefined => {
  const { ofacDetails } = response;
  const customerType = (response.customerType || '').toLowerCase();

  const getPrimaryOfacDetails = (details?: OfacPartyDetails): OfacPartyDetails => {
    if (!details) {
      return {
        ofacStatus: null,
        ofacCheckDate: null,
      };
    }

    return {
      ofacStatus: details.ofacStatus ?? null,
      ofacCheckDate: details.ofacCheckDate ?? null,
    };
  };

  const getNormalizedOfacDetails = (
    partyDetails: OfacPartyDetails | undefined,
    isSelected: boolean,
    fallbackDetails: OfacPartyDetails
  ): OfacPartyDetails => {
    const normalizedPartyDetails = getPrimaryOfacDetails(partyDetails);

    if (isSelected) {
      return normalizedPartyDetails;
    }

    return {
      ofacStatus: fallbackDetails.ofacStatus ?? null,
      ofacCheckDate: fallbackDetails.ofacCheckDate ?? null,
    };
  };

  const customerOfacDetails = getPrimaryOfacDetails(ofacDetails?.customer);
  const businessOfacDetails = getPrimaryOfacDetails(ofacDetails?.business);

  const selectedOfacDetails =
    customerType === 'private' ? customerOfacDetails : businessOfacDetails;

  const normalizedCustomer = getNormalizedOfacDetails(
    selectedOfacDetails,
    customerType === 'private',
    customerOfacDetails
  );

  const normalizedBusiness = getNormalizedOfacDetails(
    selectedOfacDetails,
    customerType === 'business',
    businessOfacDetails
  );

  const checkCompleted = ofacDetails?.checkCompleted ?? null;

  const isCustomerEmpty =
    normalizedCustomer.ofacStatus === null && normalizedCustomer.ofacCheckDate === null;
  const isBusinessEmpty =
    normalizedBusiness.ofacStatus === null && normalizedBusiness.ofacCheckDate === null;

  if (checkCompleted === null && isCustomerEmpty && isBusinessEmpty) {
    return undefined;
  }

  return {
    business: normalizedBusiness,
    customer: normalizedCustomer,
    checkCompleted,
  };
};

const formatDeliveryTimeline = (deliveryTimeline: string | undefined): string | undefined => {
  if (!deliveryTimeline) {
    return undefined;
  }

  return formatToUTCDate(deliveryTimeline);
};

/*
|--------------------------------------------------------------------------
| GET ORDERS
|--------------------------------------------------------------------------
*/

export const useGetOrders = ({
  page = 0,
  size = 10,
  searchString,
  sortField,
  sortOrder = 'desc',
  enabled = true,
}: UseGetOrdersParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.ORDERS, page, size, searchString, sortField, sortOrder],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: String(page),
        size: String(size),
      });

      const body: SearchOrdersRequest = {};
      if (searchString) body.searchString = searchString;
      if (sortField) body.sortField = sortField;
      if (sortOrder) body.sortOrder = sortOrder;

      const response = await LuppApi.post<SearchOrdersRequest, SearchOrdersResponse>(
        `sales/v1/orders/searches?${queryParams.toString()}`,
        body,
        {
          extractData: (response) => response,
        }
      );

      return {
        items: response.data,
        totalCount: response.totalCount,
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        currentPageCount: response.currentPageCount,
        totalPages: response.totalPages,
      };
    },
    placeholderData: (previousData) => previousData,
    enabled,
  });
};

/*
|--------------------------------------------------------------------------
| GET ORDER DETAILS
|--------------------------------------------------------------------------
*/

export const useGetOrderDetails = (orderIdentifier: string | null) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.ORDER_DETAILS, orderIdentifier],
    queryFn: async () => {
      if (!orderIdentifier) {
        throw new Error('Order identifier is required');
      }

      const response = await LuppApi.get<OrderDetailsResponse>(
        `sales/v1/orders/${orderIdentifier}`
      );

      return {
        ...response,
        configurationDetails: filterConfigurationDetails(response.configurationDetails),
        ofacDetails: filterOfacDetails(response),
        moduleFileReferenceDtoList: deduplicateFileReferences(response.moduleFileReferenceDtoList),
      };
    },
    enabled: !!orderIdentifier,
  });
};

/*
|--------------------------------------------------------------------------
| UPDATE ORDER
|--------------------------------------------------------------------------
*/

export const useUpdateOrder = () => {
  const queryClient = useQueryClient();

  const buildCustomerOverviewPayload = (data: UpdateOrderRequest & Record<string, unknown>) => {
    // If the data already contains a customerOverview, use it directly
    if (data.customerOverview) {
      return {
        customerOverview: data.customerOverview,
      } as CustomerOverviewPayload;
    }

    // Otherwise, transform from flat structure (backward compatibility)
    const customerType = data.customerType || 'Private';
    const normalizedCustomerType: 'Private' | 'Business' =
      customerType.toLowerCase() === 'business' ? 'Business' : 'Private';

    const cleanObject = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
      const cleaned = {} as Partial<T>;
      Object.keys(obj).forEach((key) => {
        if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
          cleaned[key as keyof T] = obj[key] as T[keyof T];
        }
      });
      return cleaned;
    };

    const payload: CustomerOverviewPayload = {
      customerOverview: {
        customerType: normalizedCustomerType, // Keep original casing (Private/Business)
        ...(data.deliveryTimeline && {
          ofacDetails: {
            deliveryTimeline: formatDeliveryTimeline(data.deliveryTimeline),
          },
        }),
      },
    };

    if (customerType.toLowerCase() === 'private') {
      payload.customerOverview.privateCustomer = {
        customerDetails: cleanObject({
          title: data.customerTitle,
          firstName: data.customerFirstName,
          middleName: data.customerMiddleName,
          lastName: data.customerLastName,
          dateOfBirth: data.customerDateOfBirth
            ? formatToUTCDate(data.customerDateOfBirth)
            : undefined,
          address: data.customerAddress,
          postalCode: data.customerPostalCode,
          city: data.customerCity,
          country: data.customerCountry,
        }),
      };
      payload.customerOverview.businessCustomer = null;
    } else if (customerType.toLowerCase() === 'business') {
      payload.customerOverview.businessCustomer = {
        customerDetails: cleanObject({
          title: data.customerTitle,
          firstName: data.customerFirstName,
          middleName: data.customerMiddleName,
          lastName: data.customerLastName,
          dateOfBirth: data.customerDateOfBirth
            ? formatToUTCDate(data.customerDateOfBirth)
            : undefined,
          address: data.customerAddress,
          postalCode: data.customerPostalCode,
          city: data.customerCity,
          country: data.customerCountry,
        }),
        businessDetails: cleanObject({
          companyName: data.companyName,
          companyTaxId: data.companyTaxId,
          companyAddress: data.companyAddress,
          postalCode: data.companyPostalCode,
          city: data.companyCity,
          country: data.companyCountry,
        }),
        registerTo: cleanObject({
          registrationProcess: data.registrationProcess,
          mailingAddressSelection: data.mailingAddressSelection,
          registrationType: data.registrationType ?? data.registrationProcess,
          registrationAddressSelection:
            data.registrationAddressSelection ?? data.mailingAddressSelection,
          address: data.registerToAddress,
          postalCode: data.registerToPostalCode,
          city: data.registerToCity,
          country: data.registerToCountry,
        }),
        deliverTo: cleanObject({
          title: data.deliverToTitle,
          firstName: data.deliverToFirstName,
          middleName: data.deliverToMiddleName,
          lastName: data.deliverToLastName,
          email: data.deliverToEmail,
        }),
      };
      payload.customerOverview.privateCustomer = null;
    }

    return payload;
  };

  return useMutation({
    mutationFn: async (data: UpdateOrderRequest) => {
      // Only build customerOverview when customer-related fields are present
      const hasCustomerData =
        data.customerOverview ||
        data.customerType ||
        data.customerFirstName ||
        data.customerLastName;

      const {
        preferredServiceCenterLocation,
        preferredSfdcServiceTerritoryId,
        deliveryAcceptanceDetails,
      } = data;

      const payload = {
        ...(hasCustomerData &&
          buildCustomerOverviewPayload(data as UpdateOrderRequest & Record<string, unknown>)),
        ...(preferredServiceCenterLocation && { preferredServiceCenterLocation }),
        ...(preferredSfdcServiceTerritoryId && { preferredSfdcServiceTerritoryId }),
        ...(deliveryAcceptanceDetails && { deliveryAcceptanceDetails }),
      };

      const response = await LuppApi.patch<Record<string, unknown>, UpdateOrderResponse>(
        `sales/v1/orders/${data.orderIdentifier}`,
        payload
      );

      return response;
    },
    onSuccess: () => {
      // Invalidate orders list and details queries
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.ORDERS] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.ORDER_DETAILS] });
    },
  });
};

/*
|--------------------------------------------------------------------------
| UPDATE FINANCE PROVIDER DETAILS
|--------------------------------------------------------------------------
*/

export const useUpdateFinanceProviderDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderIdentifier,
      data,
    }: {
      orderIdentifier: string;
      data: UpdateFinanceProviderRequest;
    }) => {
      const response = await LuppApi.patch<
        UpdateFinanceProviderRequest,
        UpdateFinanceProviderResponse
      >(`sales/v1/orders/${orderIdentifier}`, data);
      return response;
    },
    onSuccess: (_result, { orderIdentifier }) => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.ORDER_DETAILS, orderIdentifier] });
    },
  });
};

/*
|--------------------------------------------------------------------------
| UPLOAD ORDER DOCUMENT
|--------------------------------------------------------------------------
*/

export const useUploadOrderDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UploadOrderDocumentRequest) => {
      const { orderIdentifier, ...payload } = data;
      const response = await LuppApi.post<
        Omit<UploadOrderDocumentRequest, 'orderIdentifier'>,
        UploadOrderDocumentResponse
      >(`/sales/v1/orders/${orderIdentifier}/files`, payload);
      return response;
    },
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: [LUPP_QUERY_KEYS.ORDER_DETAILS, variables.orderIdentifier],
      });
    },
  });
};

/*
|--------------------------------------------------------------------------
| D&B BUSINESS SEARCH
|--------------------------------------------------------------------------
*/

const DUNS_SEARCH_BASE_URL = import.meta.env.VITE_DUNS_SEARCH_BASE_URL;
const DUNS_SEARCH_PATH = import.meta.env.VITE_DUNS_SEARCH_PATH;

/**
 * Get the full D&B search API URL
 * @throws Error if configuration is missing
 */
const getDunsSearchUrl = (): string => {
  if (!DUNS_SEARCH_BASE_URL || !DUNS_SEARCH_PATH) {
    throw new Error(
      'D&B Search API configuration is missing. Please set VITE_DUNS_SEARCH_BASE_URL and VITE_DUNS_SEARCH_PATH'
    );
  }
  return `${DUNS_SEARCH_BASE_URL}${DUNS_SEARCH_PATH}`;
};

/**
 * Transform D&B API response to flattened structure for UI consumption
 * @param response - Raw D&B API response
 * @returns Array of transformed business search results
 */
const transformDunsSearchResults = (response: DunsSearchResponse): BusinessSearchResult[] => {
  return response.data.map((item) => {
    const org = item.organization;
    const addr = org.primaryAddress;

    const street = addr?.streetAddress?.line1 || '';
    const city = addr?.addressLocality?.name || '';
    const postalCode = addr?.postalCode || '';
    const country = addr?.addressCountry?.name || '';
    const countryCode = addr?.addressCountry?.isoAlpha2Code || '';
    const region = addr?.addressRegion?.name || '';

    // Format address for form population
    const addressParts = [street, addr?.streetAddress?.line2].filter(Boolean);
    const address = addressParts.join(', ');

    // Format display text for dropdown (Name - City, Country Code)
    const locationDetails = [city, countryCode].filter(Boolean).join(', ');
    const displayText = locationDetails
      ? `${org.primaryName} - ${locationDetails}`
      : org.primaryName;

    return {
      duns: org.duns,
      name: org.primaryName,
      primaryName: org.primaryName,
      isOutOfBusiness: org.dunsControlStatus?.isOutOfBusiness ?? false,
      address,
      city,
      postalCode,
      country,
      countryCode,
      region,
      displayText,
    };
  });
};

/**
 * Search for businesses using D&B (Dun & Bradstreet) API
 *
 * @param searchTerm - Search query (minimum 3 characters)
 * @param country - ISO 3166-1 alpha-2 country code (e.g., "US", "DE")
 * @param enabled - Whether to execute the query (default: true)
 * @returns Query result with business search results
 *
 * @example
 * const { data, isLoading, error } = useSearchBusinesses('Tesla', 'DE', true);
 */
export const useSearchBusinesses = (
  searchTerm: string,
  country: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.DUNS_SEARCH, searchTerm, country],
    queryFn: async (): Promise<BusinessSearchResult[]> => {
      const apiUrl = getDunsSearchUrl();
      const idToken = getIdToken();

      const params = new URLSearchParams({
        searchTerm,
        country,
      });

      const response = await fetch(`${apiUrl}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`D&B API error: ${response.status} ${response.statusText}`);
      }

      const data: DunsSearchResponse = await response.json();
      return transformDunsSearchResults(data);
    },
    enabled: enabled && searchTerm.length >= 3 && !!country,
    staleTime: 5 * 60 * 1000, // Cache results for 5 minutes
    retry: 2, // Retry failed requests up to 2 times
  });
};

/*
|--------------------------------------------------------------------------
| GET RETAIL DELIVERY DATA
|--------------------------------------------------------------------------
*/

/**
 * Fetches retail delivery data for an order including registration and delivery details
 * @param orderIdentifier - The order identifier
 * @returns Query result with registration delivery details
 * @example
 * const { data, isLoading } = useGetRetailDeliveryData('order-123');
 */
export const useGetRetailDeliveryData = (orderIdentifier: string | null) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.RETAIL_DELIVERY, orderIdentifier],
    queryFn: async () => {
      if (!orderIdentifier) {
        throw new Error('Order identifier is required');
      }
      const response = await LuppApi.get<RegistrationDeliveryDetails>(
        `sales/v1/orders/retailDelivery/${orderIdentifier}`
      );
      return response;
    },
    enabled: Boolean(orderIdentifier),
  });
};

/*
|--------------------------------------------------------------------------
| PATCH ORDER DETAILS
|--------------------------------------------------------------------------
*/

/**
 * Mutation hook to update order details with registration and delivery information
 * @returns Mutation result
 * @example
 * const mutation = usePatchOrderDetails();
 * mutation.mutate({
 *   orderIdentifier: 'order-123',
 *   registrationDetailsPreDelivery: {
 *     scheduledDeliveryDates: {
 *       scheduledDeliveryDate: '2026-03-15',
 *       scheduledDeliveryTime: '14:30'
 *     }
 *   }
 * });
 */
export const usePatchOrderDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PatchOrderDetailsRequest) => {
      const { orderIdentifier, registrationDetailsPreDelivery } = data;

      const payload = {
        ...registrationDetailsPreDelivery,
      };

      const response = await LuppApi.patch(`sales/v1/orders/${orderIdentifier}`, {
        registrationDetailsPreDelivery: payload,
      });

      return response;
    },
    onSuccess: () => {
      // Invalidate order details and retail delivery data queries
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.ORDER_DETAILS] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.RETAIL_DELIVERY] });
    },
  });
};

/*
|--------------------------------------------------------------------------
| GET PROFORMA INVOICE LIST
|--------------------------------------------------------------------------
*/

/**
 * Fetches the list of ProForma invoices for an order
 * @param orderMasterIdentifier - The order master identifier
 * @param enabled - Whether to execute the query (default: true)
 * @returns Query result with invoice list
 */
export const useGetProFormaInvoiceList = (
  orderMasterIdentifier: string | null,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PROFORMA_INVOICES, orderMasterIdentifier],
    queryFn: async (): Promise<Invoice[]> => {
      if (!orderMasterIdentifier) {
        throw new Error('Order master identifier is required');
      }
      const response = await LuppApi.get<Invoice[]>(
        `sales/v1/orders/${orderMasterIdentifier}/proformainvoicelist`
      );
      return response;
    },
    enabled: enabled && !!orderMasterIdentifier,
  });
};

/*
|--------------------------------------------------------------------------
| DOWNLOAD PRO-FORMA INVOICE
|--------------------------------------------------------------------------
*/

/**
 * Parameters for useDownloadProFormaInvoice hook
 */
export type UseDownloadProFormaInvoiceParams = {
  orderMasterIdentifier: string;
  orderDocumentIdentifier: string;
  enabled?: boolean;
};

/**
 * Hook to download a pro-forma invoice PDF
 * @param params - Order master identifier and order document identifier
 * @returns Query result with download blob and filename
 * @example
 * const { data, isFetching } = useDownloadProFormaInvoice({
 *   orderMasterIdentifier: '4e355e32-2e3b-4a68-ad03-239e5f7d081e',
 *   orderDocumentIdentifier: '1ef57c49-3e28-4a9a-8879-e312f9785aca',
 *   enabled: true
 * });
 */
export const useDownloadProFormaInvoice = ({
  orderMasterIdentifier,
  orderDocumentIdentifier,
  enabled = true,
}: UseDownloadProFormaInvoiceParams) => {
  return useQuery({
    queryKey: [
      LUPP_QUERY_KEYS.PROFORMA_INVOICES,
      orderMasterIdentifier,
      orderDocumentIdentifier,
      'download',
    ],
    queryFn: async () => {
      const response = await LuppApi.instance.get<Blob>(
        `sales/v1/orders/${orderMasterIdentifier}/proformaDownload/${orderDocumentIdentifier}`,
        { responseType: 'blob' }
      );

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'] as string | undefined;
      const filename =
        contentDisposition?.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i)?.[1]?.trim() ||
        `proforma-invoice-${orderDocumentIdentifier}.pdf`;

      return { blob: response.data, filename: decodeURIComponent(filename) };
    },
    enabled: Boolean(orderMasterIdentifier && orderDocumentIdentifier && enabled),
  });
};

/*
|--------------------------------------------------------------------------
| CREATE INVOICE
|--------------------------------------------------------------------------
*/

/**
 * Hook to create an invoice for an order
 * @returns Mutation for creating an invoice
 * @example
 * const { mutate, isPending } = useCreateInvoice();
 * mutate({ orderIdentifier: '801e9dce-5b7a-4c3f-a9d2-1234567890ab', invoiceType: 'PROFORMA_INVOICE' });
 */
export const useCreateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInvoiceRequest) => {
      const response = await LuppApi.post<CreateInvoiceRequest, CreateInvoiceResponse>(
        `sales/v1/orders/${data.orderIdentifier}/invoices`,
        data
      );
      return response;
    },
    onSuccess: (_data, variables) => {
      if (variables.invoiceType === DOCUMENT_CATEGORY.PROFORMA_INVOICE) {
        queryClient.invalidateQueries({
          queryKey: [LUPP_QUERY_KEYS.PROFORMA_INVOICES, variables.orderIdentifier],
        });
      }
      if (variables.invoiceType === DOCUMENT_CATEGORY.FINAL_INVOICE) {
        queryClient.invalidateQueries({
          queryKey: [LUPP_QUERY_KEYS.FINAL_INVOICES, variables.orderIdentifier],
        });
      }
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.ORDER_DETAILS] });
    },
  });
};

/*
|--------------------------------------------------------------------------
| GET FINAL INVOICES
|--------------------------------------------------------------------------
*/

/**
 * Query hook to fetch final invoices for an order
 *
 * @param params - Query parameters
 * @param params.orderIdentifier - Order identifier to fetch invoices for
 * @param params.enabled - Whether the query should run (default: true)
 * @returns React Query result with final invoice documents
 * @example
 * ```tsx
 * const { data: invoices, isLoading } = useGetFinalInvoices({
 *   orderIdentifier: 'ORD-123',
 *   enabled: true
 * });
 * ```
 */
export const useGetFinalInvoices = ({
  orderIdentifier,
  enabled = true,
}: GetFinalInvoicesParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.FINAL_INVOICES, orderIdentifier],
    queryFn: async () => {
      if (!orderIdentifier) {
        throw new Error('Order identifier is required');
      }
      const response = await LuppApi.get<GetFinalInvoicesResponse>(
        `sales/v1/orders/${orderIdentifier}/finalInvoices`,
        {
          extractData: (response) => response,
        }
      );

      return response.data as Invoice[];
    },
    enabled: !!orderIdentifier && enabled,
    retry: false, // Don't retry on 404
  });
};

/*
|--------------------------------------------------------------------------
| DOWNLOAD FINAL INVOICE
|--------------------------------------------------------------------------
*/

/**
 * Mutation hook to download a final invoice PDF
 *
 * @returns React Query mutation result for downloading invoice
 * @example
 * ```tsx
 * const downloadMutation = useDownloadFinalInvoice();
 *
 * const handleDownload = () => {
 *   downloadMutation.mutate({
 *     orderIdentifier: 'ORD-123',
 *     orderDocumentIdentifier: 'DOC-456'
 *   });
 * };
 * ```
 */
export const useDownloadFinalInvoice = () => {
  return useMutation({
    mutationFn: async (params: DownloadFinalInvoiceParams) => {
      const { orderIdentifier, orderDocumentIdentifier } = params;

      // Use LuppApi instance to leverage existing interceptor for auth headers
      const response = await LuppApi.instance.get<Blob>(
        `sales/v1/orders/${orderIdentifier}/finalInvoiceDownload/${orderDocumentIdentifier}`,
        {
          responseType: 'blob',
        }
      );

      const blob = response.data;
      const contentDisposition = response.headers['content-disposition'] as string | undefined;
      let fileName = 'final-invoice.pdf';

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
        if (fileNameMatch && fileNameMatch[1]) {
          // Decode URI component, trim whitespace and remove trailing underscores
          fileName = decodeURIComponent(fileNameMatch[1].trim()).replace(/_+$/, '');
        }
      }

      return { blob, fileName };
    },
  });
};

/*
|--------------------------------------------------------------------------
| GENERATE DELIVERY ACCEPTANCE PREVIEW
|--------------------------------------------------------------------------
*/

/**
 * Generates a delivery acceptance document preview (DocuSign signing URL).
 *
 * Triggered when the agent clicks "Generate Documents" in the
 * Customer Handoff → Delivery Acceptance section.
 *
 * @returns Mutation with `view_url` for the DocuSign embedded signing experience
 * @example
 * const { mutateAsync: generatePreview, isPending } = useGenerateDeliveryAcceptancePreview();
 * const result = await generatePreview(formPayload);
 * // result.data.view_url → open in DocusignViewer
 */
export const useGenerateDeliveryAcceptancePreview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: GenerateDeliveryAcceptancePreviewRequest) => {
      const response = await LuppApi.post<
        GenerateDeliveryAcceptancePreviewRequest,
        DeliveryAcceptancePreviewDto
      >(`sales/v1/orders/${data.orderIdentifier}/delivery-acceptance`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.DELIVERY_ACCEPTANCE] });
    },
  });
};

/*
|--------------------------------------------------------------------------
| GENERATE DELIVERY ACCEPTANCE SECOND SIGNER
|--------------------------------------------------------------------------
*/

/**
 * Generates a second signer DocuSign URL for the delivery acceptance.
 *
 * Called after the first signer completes signing, using the deliveryId
 * and envelopeId from the initial preview response.
 *
 * @returns Mutation with `viewUrl` for the second signer DocuSign experience
 * @example
 * const { mutateAsync: generateSecondSigner } = useGenerateDeliveryAcceptanceSecondSigner();
 * const result = await generateSecondSigner({ orderId, deliveryId, envelopeId });
 */
export const useGenerateDeliveryAcceptanceSecondSigner = () => {
  return useMutation({
    mutationFn: async (params: { orderId: string } & DeliveryAcceptanceSecondSignerRequest) => {
      const { orderId, ...data } = params;
      const response = await LuppApi.post<
        DeliveryAcceptanceSecondSignerRequest,
        DeliveryAcceptanceSecondSignerResponse
      >(`sales/v1/orders/${orderId}/delivery-acceptance/advisor-signing`, data);
      return response;
    },
  });
};

/*
|--------------------------------------------------------------------------
| GET SERVICE CENTERS
|--------------------------------------------------------------------------
*/

/**
 * Fetches available service center locations for the service center dropdown.
 * X-Org-Id header is auto-injected by the LuppApi interceptor.
 *
 * @param params - Optional parameters for the query
 * @param params.country - Optional ISO-2 country code to filter service centers
 * @param params.enabled - Whether the query should execute (default: true)
 * @returns Query result with list of service centers
 */
export const useGetServiceCenters = (params?: { country?: string; enabled?: boolean }) => {
  const { country, enabled = true } = params ?? {};
  const queryParams = country ? `?country=${country}` : '';

  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.SERVICE_CENTERS, country],
    queryFn: async () => {
      const response = await LuppApi.get<ServiceCenter[]>(
        `sales/v1/orders/service-centers${queryParams}`
      );
      return response;
    },
    enabled,
  });
};
